import { formatMoney } from '@/utils/currency';
import { isUk, ukVatRateCode } from '@/data/locales';
import { parseReceiptTemplateSettings } from '@/utils/receiptTemplate';
/**
 * Laser / Page Printer Utilities
 * Supports HP LaserJet, Brother, Canon, Epson L-series, and other office/A4 printers.
 * Sends HP PCL 5/6 commands with explicit Form Feed (\x0C) to eject pages immediately.
 */

export function isLaserOrPagePrinter(
  printerName?: string | null,
  paperWidth?: number | string
): boolean {
  if (paperWidth === 'A4' || paperWidth === 'a4') return true;
  if (!printerName) return false;
  const lower = printerName.toLowerCase();

  // Known thermal / POS keywords — definitely NOT a laser printer
  const isThermal =
    /thermal|receipt|pos\b|label|tm-t|sprt|xprinter|zdesigner|gprinter|sunmi|58mm|80mm|black\s*copper|citizen\s*ct|star\s*tsp/i.test(
      lower
    );
  if (isThermal) return false;

  // Known laser / inkjet / page printer keywords
  return /laser|laserjet|deskjet|inkjet|mfp|page|copier|officejet|canon|brother|epson\s*l|xerox|ricoh|kyocera|konica|samsung|pantum|print\s*to\s*pdf|xps/i.test(
    lower
  );
}

/**
 * Builds standard HP PCL (Printer Command Language) byte payload for HP LaserJet
 * and PCL-compatible office printers.
 *
 * Sequence:
 * 1. \x1B\x45       -> PCL Reset (<ESC>E)
 * 2. \x1B&k2G       -> Line termination mode: CR=CR, LF=CR+LF, FF=CR+FF
 * 3. Text content   -> Formatted test page (A4 layout)
 * 4. \x0C           -> FORM FEED (ASCII 12) - triggers immediate page eject on laser printers!
 * 5. \x1B\x45       -> PCL Reset (<ESC>E)
 */
export function buildLaserTestPayload(
  header = 'SALE DESK',
  printerName = ''
): Uint8Array {
  const timeStr = new Date().toLocaleString();
  const divider = '='.repeat(76);
  const subDivider = '-'.repeat(76);

  const lines = [
    divider,
    `                      ${header.trim().toUpperCase() || 'SALE DESK'} — TEST PRINT`,
    divider,
    '',
    `  Target Printer:  ${printerName || 'HP LaserJet / Laser Page Printer'}`,
    `  Output Format:   A4 / Letter Page Mode (HP PCL Emulation)`,
    `  Spooler Driver:  Sale Desk Print Helper (Windows Spooler)`,
    `  Timestamp:       ${timeStr}`,
    `  Status:          CONNECTED & VERIFIED`,
    '',
    subDivider,
    '  [SUCCESS] PRINTER COMMUNICATION VERIFIED',
    '',
    '  If you can read this printed sheet, your HP LaserJet / Office printer is',
    '  successfully connected and ready to print from Sale Desk POS.',
    '',
    '  Laser Printing Features:',
    '  - Protocol:     HP PCL 5 / PCL 6 & Raw ASCII Spooling',
    '  - Page Feed:    Immediate Eject on Form Feed (ASCII 12 / 0x0C)',
    '  - Paper Size:   Standard A4 / Letter',
    '  - Auto Cut:     Disabled (Page-based sheet feeding)',
    subDivider,
    '',
    '                      Thank you for using Sale Desk POS!',
    divider,
    '',
  ];

  const body = lines.join('\r\n');
  const pclStream = `\x1B\x45\x1B&k2G${body}\r\n\x0C\x1B\x45`;
  return new TextEncoder().encode(pclStream);
}

/**
 * Builds a clean, crisp A4 test page as a base64 PNG image for direct silent printing via Print Helper.
 * Renders at high resolution (1240 x 1754 px @ ~150 DPI) with zero external DOM dependencies.
 */
export async function buildA4TestImageBase64(opts?: {
  header?: string;
  printerName?: string;
}): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = 1240;
  canvas.height = 1754;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D canvas context');

  const header = opts?.header || 'SALE DESK';
  const printerName = opts?.printerName || 'HP LaserJet / Office Printer';
  const now = new Date().toLocaleString();

  // Background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Top color accent bar
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(0, 0, canvas.width, 18);

  // Header
  ctx.fillStyle = '#0f172a';
  ctx.font = 'bold 42px sans-serif';
  ctx.fillText(`${header} — TEST PRINT`, 80, 105);

  ctx.fillStyle = '#64748b';
  ctx.font = '20px sans-serif';
  ctx.fillText('Laser / Office Printer Direct Silent Print Verification (Black & White)', 80, 142);

  // Divider
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(80, 170);
  ctx.lineTo(1160, 170);
  ctx.stroke();

  // Information Grid
  ctx.font = '20px sans-serif';
  const drawRow = (label: string, val: string, y: number) => {
    ctx.fillStyle = '#64748b';
    ctx.fillText(label, 80, y);
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText(val, 280, y);
    ctx.font = '20px sans-serif';
  };

  drawRow('Printer Name:', printerName, 225);
  drawRow('Driver Spooler:', 'Sale Desk Print Helper v1.3.0 (.NET GDI Spooler)', 265);
  drawRow('Paper Format:', 'Standard A4 Page (210 x 297 mm)', 305);
  drawRow('Timestamp:', now, 345);
  drawRow('Print Mode:', 'Direct Silent Spooling (Zero Dialogs)', 385);

  // Success Box (Monochrome Slate/Black background with border)
  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(80, 435, 1080, 150);
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 2;
  ctx.strokeRect(80, 435, 1080, 150);

  ctx.fillStyle = '#0f172a';
  ctx.font = 'bold 26px sans-serif';
  ctx.fillText('✓ SUCCESSFUL DIRECT A4 PRINT', 120, 490);

  ctx.fillStyle = '#334155';
  ctx.font = '19px sans-serif';
  ctx.fillText('If you are reading this printed sheet, your printer communication is 100% verified.', 120, 528);
  ctx.fillText('Sale Desk POS can now print invoices silently to this printer with zero browser dialogs.', 120, 558);

  // Sample Items Table (to verify crisp font rendering)
  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(80, 620, 1080, 42);
  ctx.strokeStyle = '#0f172a';
  ctx.strokeRect(80, 620, 1080, 42);

  ctx.fillStyle = '#0f172a';
  ctx.font = 'bold 17px sans-serif';
  ctx.fillText('#', 100, 647);
  ctx.fillText('Sample Description', 160, 647);
  ctx.fillText('Qty', 700, 647);
  ctx.fillText('Rate', 850, 647);
  ctx.fillText('Total', 1040, 647);

  const drawTableRow = (idx: string, desc: string, qty: string, rate: string, total: string, y: number) => {
    ctx.fillStyle = '#1e293b';
    ctx.font = '17px sans-serif';
    ctx.fillText(idx, 100, y);
    ctx.fillText(desc, 160, y);
    ctx.fillText(qty, 700, y);
    ctx.fillText(rate, 850, y);
    ctx.fillText(total, 1040, y);
    ctx.strokeStyle = '#e2e8f0';
    ctx.beginPath();
    ctx.moveTo(80, y + 14);
    ctx.lineTo(1160, y + 14);
    ctx.stroke();
  };

  drawTableRow('1', 'Direct Silent Print Verification Item A', '1', 'Rs 500.00', 'Rs 500.00', 695);
  drawTableRow('2', 'High-Resolution GDI Driver Rendering Item B', '2', 'Rs 250.00', 'Rs 500.00', 740);

  // Total summary line
  ctx.fillStyle = '#0f172a';
  ctx.font = 'bold 20px sans-serif';
  ctx.fillText('Total Test Amount:', 750, 800);
  ctx.fillText('Rs 1,000.00', 1020, 800);

  // Footer
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, 1650);
  ctx.lineTo(1160, 1650);
  ctx.stroke();

  ctx.fillStyle = '#94a3b8';
  ctx.font = '16px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(`Generated by Sale Desk POS · Native Windows Print Helper · ${now}`, 620, 1690);
  ctx.textAlign = 'left';

  const dataUrl = canvas.toDataURL('image/png');
  return dataUrl.split(',')[1];
}

/**
 * Converts any DOM receipt element to an A4 base64 PNG string for direct silent printing.
 * Selects the inner .invoice element directly to eliminate unnecessary wrapper borders & padding.
 */
export async function receiptElementToA4ImageBase64(
  source: HTMLElement,
  opts?: { width?: number }
): Promise<string> {
  const { captureReceiptPng } = await import('@/utils/shareReceiptImage');
  const target = (source.querySelector('.invoice') as HTMLElement) || source;
  const { dataUrl } = await captureReceiptPng(target, {
    width: opts?.width || 720,
  });
  return dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
}

function openPrintIframe(htmlBody: string, extraHead = ''): void {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.left = '-9999px';
  iframe.style.top = '0';
  iframe.style.width = '794px';
  iframe.style.height = '1123px';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    window.print();
    return;
  }

  doc.open();
  doc.write(`<!DOCTYPE html><html><head><title>Invoice</title>${extraHead}</head><body>${htmlBody}</body></html>`);
  doc.close();

  setTimeout(() => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      window.print();
    }
    setTimeout(() => {
      try {
        if (iframe.parentNode) document.body.removeChild(iframe);
      } catch { /* ignore */ }
    }, 60000);
  }, 350);
}

/**
 * Plain UK tax-invoice A4 browser print (white page, black rules, no colour fills).
 * Prefer this for laser / A4 — does not clone the on-screen coloured receipt template.
 */
export function printPlainA4Invoice(
  inv: any,
  companyName?: string,
  posSettings?: any,
): void {
  const html = generateInvoiceA4Html(inv, companyName, posSettings);
  openPrintIframe(html, `
    <style>
      @page { size: A4 portrait; margin: 10mm; }
      html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    </style>
  `);
}

/**
 * Clean isolated iframe browser print for receipts / invoices.
 * Prints ONLY the invoice element inside an isolated iframe, completely eliminating
 * the entire background page (SalesHubPage / POSPage) so it NEVER prints 7 pages!
 */
export function printReceiptInCleanIframe(sourceElement: HTMLElement): void {
  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((s) => s.outerHTML)
    .join('\n');

  // Determine whether sourceElement is .invoice-wrapper or contains it
  const wrapper = sourceElement.classList.contains('invoice-wrapper')
    ? sourceElement
    : (sourceElement.closest('.invoice-wrapper') || sourceElement);

  openPrintIframe(
    `<div class="invoice-wrapper">${wrapper.innerHTML}</div>`,
    `
      ${styles}
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        html, body {
          margin: 0 !important; padding: 0 !important; background: #fff !important;
          height: auto !important; overflow: visible !important;
        }
        .invoice-wrapper {
          background: #fff !important; padding: 0 !important; margin: 0 auto !important;
          width: 100% !important; display: flex !important; justify-content: center !important;
        }
        .invoice-wrapper .invoice {
          box-shadow: none !important; border-radius: 0 !important; margin: 0 auto !important;
          max-width: 680px !important; width: 100% !important;
        }
        .invoice-wrapper table.items { display: table !important; width: 100% !important; }
        .invoice-wrapper .items-mobile { display: none !important; }
        /* Force Classic layout plain on print even if stale CSS is present */
        .invoice-wrapper .invoice.layout-standard .top-bar { display: none !important; }
        .invoice-wrapper .invoice.layout-standard .meta-container,
        .invoice-wrapper .invoice.layout-standard .paid-box,
        .invoice-wrapper .invoice.layout-standard .status-box,
        .invoice-wrapper .invoice.layout-standard .status-box .check,
        .invoice-wrapper .invoice.layout-standard table.items thead th,
        .invoice-wrapper .invoice.layout-standard table.items tbody td {
          background: transparent !important; background-color: transparent !important;
          color: #111 !important; border-radius: 0 !important;
        }
        .invoice-wrapper .invoice.layout-standard .paid-box { color: #111 !important; padding: 6px 0 !important; }
        .invoice-wrapper .invoice.layout-standard .meta-container { border: none !important; padding: 8px 0 !important; }
        .invoice-wrapper .invoice.layout-standard table.items thead th {
          border-top: 1px solid #111 !important; border-bottom: 1px solid #111 !important;
        }
        .invoice-wrapper .invoice.layout-standard .contact-info .icon,
        .invoice-wrapper .invoice.layout-standard .meta-icon { display: none !important; }
      </style>
    `,
  );
}

/**
 * Builds a clean A4 test page as a base64 PDF for direct silent printing via Print Helper.
 */
export async function buildA4TestPdfBase64(opts?: {
  header?: string;
  printerName?: string;
}): Promise<string> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });
  const header = opts?.header || 'SALE DESK';
  const printerName = opts?.printerName || 'HP LaserJet / Office Printer';
  const now = new Date().toLocaleString();

  doc.setFontSize(22);
  doc.setTextColor(30, 41, 59);
  doc.text(`${header} — TEST PRINT`, 20, 30);

  doc.setFontSize(12);
  doc.setTextColor(100, 116, 139);
  doc.text('Laser / Office Printer Direct Silent Print Verification', 20, 38);

  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.5);
  doc.line(20, 44, 190, 44);

  doc.setFontSize(11);
  doc.setTextColor(51, 65, 85);
  doc.text(`Printer Name:    ${printerName}`, 20, 56);
  doc.text(`Spooler Driver:  Sale Desk Print Helper (POST /print-pdf)`, 20, 64);
  doc.text(`Paper Format:    Standard A4 Sheet (210 x 297 mm)`, 20, 72);
  doc.text(`Timestamp:       ${now}`, 20, 80);
  doc.text(`Print Mode:      Direct Silent Spooling (Zero Dialogs)`, 20, 88);

  doc.setFillColor(241, 245, 249);
  doc.roundedRect(20, 100, 170, 32, 3, 3, 'F');

  doc.setFontSize(12);
  doc.setTextColor(16, 185, 129);
  doc.text('✓ SUCCESSFUL DIRECT A4 PRINT', 26, 112);

  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text('If you are reading this page, your printer is configured for seamless', 26, 120);
  doc.text('one-click A4 invoice printing without any browser popups.', 26, 126);

  doc.line(20, 270, 190, 270);
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  doc.text(`Generated by Sale Desk POS · ${now}`, 105, 276, { align: 'center' });

  return doc.output('datauristring').split(',')[1];
}

/**
 * Converts any DOM receipt element to an A4 PDF base64 string for direct silent printing.
 */
export async function receiptElementToA4PdfBase64(
  source: HTMLElement,
  opts?: { width?: number }
): Promise<string> {
  const { captureReceiptPng } = await import('@/utils/shareReceiptImage');
  const { jsPDF } = await import('jspdf');

  const { dataUrl } = await captureReceiptPng(source, {
    width: opts?.width || 680,
  });

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const imgProps = pdf.getImageProperties(dataUrl);
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const marginX = 12;
  const marginY = 12;
  const printWidth = pdfWidth - marginX * 2;
  const printHeight = (imgProps.height * printWidth) / imgProps.width;

  if (printHeight <= pdfHeight - marginY * 2) {
    pdf.addImage(dataUrl, 'PNG', marginX, marginY, printWidth, printHeight);
  } else {
    let heightLeft = printHeight;
    let position = marginY;
    pdf.addImage(dataUrl, 'PNG', marginX, position, printWidth, printHeight);
    heightLeft -= (pdfHeight - marginY * 2);

    while (heightLeft > 0) {
      position = heightLeft - printHeight + marginY;
      pdf.addPage();
      pdf.addImage(dataUrl, 'PNG', marginX, position, printWidth, printHeight);
      heightLeft -= (pdfHeight - marginY * 2);
    }
  }

  return pdf.output('datauristring').split(',')[1];
}

function escapeA4Html(s: any): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function generateInvoiceA4Html(inv: any, fallbackCompany?: string, posSettings?: any): string {
  const company = inv?.company_name || fallbackCompany || 'Sale Desk';
  // Drop accidental JS/source leaks that sometimes land in company profile fields
  const scrub = (v: unknown) => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    if (
      /\b(const|let|var|function|import|export|return)\b/.test(s)
      || /[{};=<>]/.test(s)
      || /\?.*:/.test(s)
      || /subtotalDisplay|promoTotals|showPromoRow|frappe\./i.test(s)
    ) {
      return '';
    }
    return s;
  };
  const address = scrub(inv?.company_address);
  const phone = scrub(inv?.company_phone);
  const email = scrub(inv?.company_email);
  const logo = inv?.company_logo;
  const customer = inv?.customer_name || inv?.customer || 'Walk-in Customer';
  const cashier = inv?.cashier_name || inv?.cashier || 'Cashier';
  const dateStr = new Date(inv?.creation || inv?.invoice_date || Date.now()).toLocaleString();
  const birthYear = inv?.customer_birth_year ? `Customer YOB: ${inv.customer_birth_year}` : '';
  const tmpl = parseReceiptTemplateSettings(posSettings?.receipt_template_settings);
  const showVatRate = Boolean(tmpl.show_vat_rate) && isUk(posSettings?.country);

  const subtotal = inv?.item_total ?? (inv?.net_total ?? 0);
  const discount = inv?.discount_amount || 0;
  const writeOff = inv?.write_off_amount || 0;
  const tax = inv?.tax_amount || 0;
  const total = inv?.net_total ?? (subtotal - discount - writeOff + tax);
  const paid = inv?.paid_amount != null ? inv.paid_amount : total;
  const balance = Math.max(0, total - paid);

  const items = inv?.items || [];
  const payments = inv?.payments || [];

  return `
    <div id="invoice-pdf-root" style="width:794px; min-height:auto; display:flex; flex-direction:column; justify-content:space-between; padding:28px 32px 18px 32px; background:#fff; color:#111; font-family:Arial,Helvetica,sans-serif; box-sizing:border-box;">
      <div>
        <!-- Header — plain UK tax invoice -->
        <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:12px; padding-bottom:10px; border-bottom:1px solid #111;">
          <div style="width:55%;">
            ${logo ? `<img src="${escapeA4Html(logo)}" style="max-height:48px; margin-bottom:6px; object-fit:contain;" />` : ''}
            <div style="font-size:18px; font-weight:700; color:#111; margin-bottom:4px; font-family:'Times New Roman',Times,serif;">${escapeA4Html(company)}</div>
            <div style="font-size:11px; color:#333; line-height:1.4;">
              ${address ? `${escapeA4Html(address)}<br>` : ''}
              ${phone ? `Tel: ${escapeA4Html(phone)}` : ''}${phone && email ? ' · ' : ''}${email ? `Email: ${escapeA4Html(email)}` : ''}
            </div>
          </div>
          <div style="width:42%; display:flex; flex-direction:column; align-items:flex-end;">
            <div style="font-size:16px; font-weight:700; color:#111; letter-spacing:0.06em; margin-bottom:8px; font-family:'Times New Roman',Times,serif;">TAX INVOICE</div>
            <table style="font-size:11px; text-align:right;">
              <tr>
                <td style="color:#333; padding-bottom:3px; padding-right:10px;">Invoice No:</td>
                <td style="font-weight:700; color:#111; padding-bottom:3px;">${escapeA4Html(inv?.name || 'INV')}</td>
              </tr>
              <tr>
                <td style="color:#333; padding-bottom:3px; padding-right:10px;">Date:</td>
                <td style="font-weight:600; color:#111; padding-bottom:3px;">${escapeA4Html(dateStr)}</td>
              </tr>
              <tr>
                <td style="color:#333; padding-bottom:3px; padding-right:10px;">Served By:</td>
                <td style="font-weight:600; color:#111; padding-bottom:3px;">${escapeA4Html(cashier)}</td>
              </tr>
            </table>
          </div>
        </div>

        <!-- Customer -->
        <div style="margin-bottom:12px; padding:0 0 8px 0; border-bottom:1px solid #ccc; display:flex; justify-content:space-between; align-items:flex-end;">
          <div>
            <div style="font-size:10px; font-weight:700; color:#111; text-transform:uppercase; letter-spacing:0.04em;">Billed To</div>
            <div style="font-size:13px; font-weight:700; color:#111;">${escapeA4Html(customer)}</div>
          </div>
          ${birthYear ? `<div style="font-size:11px; color:#333;">YOB: ${escapeA4Html(birthYear)}</div>` : ''}
        </div>

        <!-- Items Table -->
        <table style="width:100%; border-collapse:collapse; font-size:11px; margin-bottom:12px;">
          <thead>
            <tr>
              <th style="text-align:left; padding:6px 4px; color:#111; font-weight:700; width:${showVatRate ? '42%' : '50%'}; font-size:10px; text-transform:uppercase; border-top:1px solid #111; border-bottom:1px solid #111; background:transparent;">Item description</th>
              <th style="text-align:center; padding:6px 4px; color:#111; font-weight:700; width:12%; font-size:10px; text-transform:uppercase; border-top:1px solid #111; border-bottom:1px solid #111; background:transparent;">Qty</th>
              <th style="text-align:right; padding:6px 4px; color:#111; font-weight:700; width:18%; font-size:10px; text-transform:uppercase; border-top:1px solid #111; border-bottom:1px solid #111; background:transparent;">Unit price</th>
              <th style="text-align:right; padding:6px 4px; color:#111; font-weight:700; width:18%; font-size:10px; text-transform:uppercase; border-top:1px solid #111; border-bottom:1px solid #111; background:transparent;">Amount</th>
              ${showVatRate ? `<th style="text-align:right; padding:6px 4px; color:#111; font-weight:700; width:10%; font-size:10px; text-transform:uppercase; border-top:1px solid #111; border-bottom:1px solid #111; background:transparent;">VAT Rate</th>` : ''}
            </tr>
          </thead>
          <tbody>
            ${items.map((it: any) => {
              const vatCode = ukVatRateCode(it.tax_rate);
              return `
              <tr>
                <td style="padding:6px 4px; color:#111; border-bottom:1px solid #ccc;">
                  <div style="font-weight:600; font-size:11.5px;">${escapeA4Html(it.item_name || it.item_code || '')}${it.variant_name ? ` - ${escapeA4Html(it.variant_name)}` : ''}</div>
                  ${it.item_code && it.item_code !== it.item_name ? `<div style="font-size:9.5px; color:#555;">${escapeA4Html(it.item_code)}</div>` : ''}
                  ${it.line_comment ? `<div style="font-size:10px; color:#444; font-style:italic; margin-top:2px;">${escapeA4Html(it.line_comment)}</div>` : ''}
                </td>
                <td style="padding:6px 4px; text-align:center; color:#111; font-size:11.5px; border-bottom:1px solid #ccc;">${escapeA4Html(it.quantity || it.qty || 1)}</td>
                <td style="padding:6px 4px; text-align:right; color:#111; font-size:11.5px; border-bottom:1px solid #ccc;">${formatMoney(Number(it.rate) || 0)}</td>
                <td style="padding:6px 4px; text-align:right; color:#111; font-weight:700; font-size:11.5px; border-bottom:1px solid #ccc;">${formatMoney(Number(it.amount || (it.rate * it.quantity)) || 0)}</td>
                ${showVatRate ? `<td style="padding:6px 4px; text-align:right; color:#111; font-weight:700; font-size:11.5px; border-bottom:1px solid #ccc;">${escapeA4Html(vatCode)}</td>` : ''}
              </tr>`;
            }).join('')}
          </tbody>
        </table>

        <!-- Totals & Payments -->
        <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:14px;">
          <div style="width:48%; font-size:10.5px;">
            ${payments.length > 0 ? `
              <div style="font-weight:700; color:#111; margin-bottom:4px; text-transform:uppercase; font-size:9.5px; letter-spacing:0.04em;">Payment</div>
              ${payments.map((p: any) => `
                <div style="display:flex; justify-content:space-between; padding:3px 0; border-bottom:1px solid #ccc;">
                  <span style="color:#333;">${escapeA4Html(p.payment_method || 'Cash')}${p.reference_no ? ` (${escapeA4Html(p.reference_no)})` : ''}</span>
                  <span style="font-weight:600; color:#111;">${formatMoney(Number(p.amount) || 0)}</span>
                </div>
              `).join('')}
            ` : ''}
            ${inv?.notes ? `
              <div style="margin-top:8px; padding:0; font-size:10.5px; color:#333;">
                <strong>Notes:</strong> ${escapeA4Html(inv.notes)}
              </div>` : ''}
          </div>

          <table style="width:260px; font-size:11.5px; border-collapse:collapse;">
            <tr>
              <td style="padding:4px 0; color:#111;">Subtotal</td>
              <td style="padding:4px 0; text-align:right; font-weight:600; color:#111;">${formatMoney(Number(subtotal) || 0)}</td>
            </tr>
            ${discount > 0 ? `
            <tr>
              <td style="padding:4px 0; color:#111;">Discount</td>
              <td style="padding:4px 0; text-align:right; font-weight:600; color:#111;">− ${formatMoney(Number(discount) || 0)}</td>
            </tr>` : ''}
            ${writeOff > 0 ? `
            <tr>
              <td style="padding:4px 0; color:#111;">Write off</td>
              <td style="padding:4px 0; text-align:right; font-weight:600; color:#111;">− ${formatMoney(Number(writeOff) || 0)}</td>
            </tr>` : ''}
            ${tax > 0 ? `
            <tr>
              <td style="padding:4px 0; color:#111;">VAT</td>
              <td style="padding:4px 0; text-align:right; font-weight:600; color:#111;">${formatMoney(Number(tax) || 0)}</td>
            </tr>` : ''}
            <tr style="border-top:1px solid #111; border-bottom:2px solid #111;">
              <td style="padding:6px 0; font-size:12.5px; font-weight:700; color:#111;">Total</td>
              <td style="padding:6px 0; text-align:right; font-size:13px; font-weight:700; color:#111;">${formatMoney(Number(total) || 0)}</td>
            </tr>
            <tr>
              <td style="padding:4px 0; color:#111;">Amount paid</td>
              <td style="padding:4px 0; text-align:right; font-weight:600; color:#111;">${formatMoney(Number(paid) || 0)}</td>
            </tr>
            ${balance > 0 ? `
            <tr>
              <td style="padding:4px 0; color:#111; font-weight:700;">Balance due</td>
              <td style="padding:4px 0; text-align:right; font-weight:700; color:#111;">${formatMoney(Number(balance) || 0)}</td>
            </tr>` : ''}
          </table>
        </div>
      </div>

      <div style="border-top:1px solid #111; padding-top:10px; text-align:center; font-size:10px; color:#555;">
        Thank you for your business · Powered by SaleDesk POS
      </div>
    </div>
  `;
}

/**
 * Converts a POS receipt / invoice object into an A4 PDF base64 string.
 */
export async function invoiceToA4PdfBase64(
  inv: any,
  companyName?: string,
  posSettings?: any
): Promise<string> {
  const [html2canvas, { jsPDF }] = await Promise.all([
    import('html2canvas').then((m) => m.default),
    import('jspdf'),
  ]);

  const html = generateInvoiceA4Html(inv, companyName, posSettings);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.width = '794px';
  container.style.zIndex = '-9999';
  container.style.opacity = '0';
  container.style.pointerEvents = 'none';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const root = container.querySelector('#invoice-pdf-root') as HTMLElement;
    const canvas = await html2canvas(root || container, {
      scale: 2,
      useCORS: true,
      width: 794,
      windowWidth: 794,
    });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4',
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    return pdf.output('datauristring').split(',')[1];
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Converts a POS receipt / invoice object into a high-resolution A4 PNG base64 string
 * for direct silent printing via Print Helper with native Windows GDI PrintDocument spooler.
 */
export async function invoiceToA4ImageBase64(
  inv: any,
  companyName?: string,
  posSettings?: any
): Promise<string> {
  const html2canvas = await import('html2canvas').then((m) => m.default);
  const html = generateInvoiceA4Html(inv, companyName, posSettings);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.width = '794px';
  container.style.zIndex = '-9999';
  container.style.opacity = '0';
  container.style.pointerEvents = 'none';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const root = container.querySelector('#invoice-pdf-root') as HTMLElement;
    const canvas = await html2canvas(root || container, {
      scale: 2,
      useCORS: true,
      width: 794,
      windowWidth: 794,
    });
    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  } finally {
    try {
      document.body.removeChild(container);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Trigger an A4 browser print test page using a hidden iframe.
 * Guaranteed to work with host-based GDI / XPS drivers that don't interpret raw PCL.
 */
export function browserPrintTestPage(opts?: {
  header?: string;
  printerName?: string;
}): void {
  const header = opts?.header || 'Sale Desk';
  const printerName = opts?.printerName || 'Default Windows Printer';
  const now = new Date().toLocaleString();

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.left = '-9999px';
  iframe.style.top = '0';
  iframe.style.width = '794px';
  iframe.style.height = '1123px';
  iframe.style.border = '0';
  iframe.style.opacity = '0';

  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    window.print();
    return;
  }

  doc.open();
  doc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Sale Desk — Printer Test Page</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 15mm;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            color: #111827;
            padding: 24px;
            margin: 0;
            background: #fff;
          }
          .box {
            border: 2px solid #111827;
            border-radius: 12px;
            padding: 32px;
            max-width: 720px;
            margin: 0 auto;
          }
          .header {
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 16px;
            margin-bottom: 24px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          .title {
            font-size: 24px;
            font-weight: 700;
            margin: 0;
            color: #111827;
          }
          .subtitle {
            font-size: 13px;
            color: #6b7280;
            margin-top: 4px;
          }
          .badge {
            display: inline-block;
            background: #ecfdf5;
            color: #065f46;
            border: 1px solid #a7f3d0;
            padding: 6px 12px;
            border-radius: 9999px;
            font-size: 12px;
            font-weight: 600;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 140px 1fr;
            gap: 10px 16px;
            font-size: 14px;
            margin-bottom: 24px;
          }
          .label {
            color: #6b7280;
            font-weight: 500;
          }
          .value {
            color: #111827;
            font-weight: 600;
          }
          .status-banner {
            background: #f9fafb;
            border: 1px dashed #d1d5db;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 24px;
          }
          .status-title {
            font-size: 14px;
            font-weight: 600;
            color: #059669;
            margin: 0 0 6px 0;
          }
          .status-desc {
            font-size: 13px;
            color: #4b5563;
            line-height: 1.5;
            margin: 0;
          }
          .footer {
            border-top: 1px solid #e5e7eb;
            padding-top: 16px;
            margin-top: 32px;
            font-size: 12px;
            color: #9ca3af;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <div class="box">
          <div class="header">
            <div>
              <h1 class="title">${header} — Test Page</h1>
              <p class="subtitle">Laser / Office Printer Verification</p>
            </div>
            <span class="badge">A4 Sheet OK</span>
          </div>

          <div class="info-grid">
            <div class="label">Printer Name:</div>
            <div class="value">${printerName}</div>
            <div class="label">Print Mode:</div>
            <div class="value">Standard Office Page (A4 Portrait)</div>
            <div class="label">Date & Time:</div>
            <div class="value">${now}</div>
            <div class="label">Connection:</div>
            <div class="value">Windows Spooler / Browser Driver</div>
          </div>

          <div class="status-banner">
            <h3 class="status-title">✓ Printer Output Test Successful</h3>
            <p class="status-desc">
              If this test page printed cleanly with sharp text and proper margins, your HP LaserJet / Office printer is configured and ready for Sale Desk POS quotations, sales invoices, and reports.
            </p>
          </div>

          <div class="footer">
            Sale Desk POS · Office Printer Test Sheet · Generated on ${now}
          </div>
        </div>
      </body>
    </html>
  `);
  doc.close();

  setTimeout(() => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      window.print();
    }
  }, 250);

  setTimeout(() => {
    try {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
    } catch {
      /* ignore */
    }
  }, 60000);
}

/**
 * Generates and downloads a 1-click Windows batch setup file that
 * automatically detects Chrome or Edge and creates a desktop shortcut
 * with the `--kiosk-printing` flag enabled for 100% direct silent printing.
 */
export function downloadKioskShortcutScript(appUrl?: string): void {
  const targetUrl = appUrl || window.location.origin;
  const scriptContent = `@echo off
setlocal
set "URL=${targetUrl}"

echo ========================================================
echo  Sale Desk POS - 1-Click Direct Print Shortcut Setup
echo ========================================================
echo.

set "BROWSER_PATH="
if exist "%ProgramFiles%\\Google\\Chrome\\Application\\chrome.exe" (
    set "BROWSER_PATH=%ProgramFiles%\\Google\\Chrome\\Application\\chrome.exe"
) else if exist "%ProgramFiles(x86)%\\Google\\Chrome\\Application\\chrome.exe" (
    set "BROWSER_PATH=%ProgramFiles(x86)%\\Google\\Chrome\\Application\\chrome.exe"
) else if exist "%LocalAppData%\\Google\\Chrome\\Application\\chrome.exe" (
    set "BROWSER_PATH=%LocalAppData%\\Google\\Chrome\\Application\\chrome.exe"
) else if exist "%ProgramFiles(x86)%\\Microsoft\\Edge\\Application\\msedge.exe" (
    set "BROWSER_PATH=%ProgramFiles(x86)%\\Microsoft\\Edge\\Application\\msedge.exe"
) else if exist "%ProgramFiles%\\Microsoft\\Edge\\Application\\msedge.exe" (
    set "BROWSER_PATH=%ProgramFiles%\\Microsoft\\Edge\\Application\\msedge.exe"
)

if "%BROWSER_PATH%"=="" (
    echo [ERROR] Chrome or Edge was not detected automatically.
    echo Please make sure Google Chrome or Microsoft Edge is installed.
    pause
    exit /b 1
)

set SCRIPT="%TEMP%\\CreatePOSShortcut.vbs"
echo Set oWS = WScript.CreateObject("WScript.Shell") > %SCRIPT%
echo sLinkFile = oWS.SpecialFolders("Desktop") ^& "\\SaleDesk POS (Direct Print).lnk" >> %SCRIPT%
echo Set oLink = oWS.CreateShortcut(sLinkFile) >> %SCRIPT%
echo oLink.TargetPath = "%BROWSER_PATH%" >> %SCRIPT%
echo oLink.Arguments = "--kiosk-printing " ^& Chr(34) ^& "%URL%" ^& Chr(34) >> %SCRIPT%
echo oLink.Description = "Sale Desk POS with Direct Silent Printing" >> %SCRIPT%
echo oLink.Save >> %SCRIPT%

cscript /nologo %SCRIPT%
del %SCRIPT%

echo.
echo [SUCCESS] Desktop shortcut created: "SaleDesk POS (Direct Print)"
echo Launch POS from that shortcut to print directly without any print window!
echo.
pause
`;

  const blob = new Blob([scriptContent], { type: 'application/x-bat' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Setup-Direct-Print.bat';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
